import type { SmartRetryError } from '~/shared/smart-restart'

import { Events } from '@setplex/pureya'
import { api } from '@setplex/tria-api'
import { createEffect, createEvent, createStore, sample } from 'effector'
import { runtime } from '~/app/environment'
import { reloadUrl } from '~/entities/player/model'
import { entitiesPlayerTrailerModel } from '~/entities/player/trailer'
import { mq, PLAYER } from '~/shared/mq'
import { SmartRestartController } from '~/shared/smart-restart'

/**
 * True when at least one of the stream's DRM systems is playable on this
 * platform. `runtime.drms` holds the platform-supported DRM types (uppercase,
 * e.g. WIDEVINE/PLAYREADY/FAIRPLAY); the url-response `drm.la` is keyed by the
 * same type names. When none match, the DRM stream is unsupported (CR-404 AC-6).
 */
function isDrmSupportedByPlatform(la: Record<string, unknown>): boolean {
  const supported = new Set(runtime.drms.map((type) => type.toUpperCase()))
  return Object.keys(la).some((type) => supported.has(type.toUpperCase()))
}

// Can be toggled by the developer to disable smart restart (or accept feature flag later).
export const $enabled = createStore<boolean>(true)

// Events driven by the adapter

/** Recoverable player error detected; drives auto-retry state machine. */
export const errorDetected = createEvent<SmartRetryError>()

/** Playback resumed after a successful retry; clears retry counter. */
export const playbackRestored = createEvent()

/** User switched content; preserves per-content counts, resets step. */
export const contentSwitched = createEvent<string | number | undefined>()

/** Manual Retry button pressed; ERROR → RETRYING. */
export const manualRetryStarted = createEvent()

/** Source load started; carries whether the pipeline is native (no MSE/VHS). */
export const loadStarted = createEvent<{ isNative: boolean }>()

/**
 * DRM status derived from the url response; updates the retry budget limit and
 * whether the stream's DRM is supported by the platform. `isSupported` defaults
 * to true when omitted.
 */
export const drmDetected = createEvent<{
  isDrm: boolean
  isSupported?: boolean
}>()

/** Feature mounting signal wired in processes/launch.ts. */
export const init = createEvent()

/** User left the player page; resets all retry state so the next visit starts fresh. */
export const playerLeft = createEvent()

/**
 * Count of playback-URL reloads (`reloadUrl`) in the current player session,
 * reset when the player is left.
 *
 * NOTE: this no longer drives the player source string. Cache-busting the source
 * (so the player reloads a byte-identical URL after a retry or a same-URL channel
 * switch) is handled by `usePlaybackUrlWithRestartAttempt`, which derives its
 * `#restart-{n}` fragment from the URL-fetch settle in the SAME render as the
 * URL — an effector counter bumped here would change before the rendered URL and
 * reload the previous content's URL. This store is kept as an observable
 * reload-attempt counter.
 */
export const $restartAttempt = createStore(0)
  .on(reloadUrl, (attempt) => attempt + 1)
  .reset(playerLeft)

/** Append `#restart-{attempt}` after query params; replace any existing `#…` fragment. */
export function appendRestartAttempt(
  playbackUrl: string,
  attempt: number
): string {
  if (attempt === 0) return playbackUrl

  const base = playbackUrl.replace(/#.*$/, '')
  return `${base}#restart-${attempt}`
}

// Retry state machine: onRetry reloads on timer; onStepChange emits SmartRestartError for the countdown UI.
const controller = new SmartRestartController({
  isDrm: false,
  onRetry: () => {
    reloadUrl()
  },
  onStepChange: (step) => {
    if (step === 'RETRYING') {
      mq.emit(PLAYER, { type: Events.Player.SmartRestartRetrying })
    }
    if (step === 'ERROR') {
      mq.emit(PLAYER, {
        type: Events.Player.SmartRestartError,
        payload: { cooldownMs: controller.remainingRetryDelayMs ?? 0 },
      })
    }
  },
  onRecoveryAbort: () => {
    mq.emit(PLAYER, { type: Events.Player.SmartRestartAborted })
  },
})

// Effects that forward Effector events to controller.

const handleErrorFx = createEffect((error: SmartRetryError) => {
  controller.handleError(error)
})

const handlePlaybackRestoredFx = createEffect(() => {
  const wasRestoring = controller.handlePlaybackRestored()
  if (wasRestoring) {
    mq.emit(PLAYER, { type: Events.Player.SmartRestartSuccess })
  }
})

const handleContentSwitchedFx = createEffect(
  (_contentId: string | number | undefined) => {
    controller.handleContentSwitch()
  }
)

const handleManualRetryStartedFx = createEffect(() => {
  controller.handleManualRetryStart()
})

const handleDrmDetectedFx = createEffect(
  ({ isDrm, isSupported }: { isDrm: boolean; isSupported?: boolean }) => {
    controller.handleDrm(isDrm, isSupported ?? true)
  }
)

const handlePlayerLeftFx = createEffect(() => {
  controller.reset()
})

// If current content actually has a playback url, we can retry.
const $hasPlaybackUrl = createStore<boolean>(false)
  .on(
    api.player.$currentPlayerPageUrl,
    (current, url) => current || Boolean(url?.playbackUrl)
  )
  .reset([contentSwitched, playerLeft])

// Native-mode is captured on `loadstart` (before the error overlay's
// resetSoft() drops the tech's VHS handler) and reused when errors arrive.
// Defaults to false so streams keep their normal auto-retry budget until a
// load proves the pipeline is native (CR-404 AC-9 / step 1.2).
const $isNativeSource = createStore<boolean>(false)
  .on(loadStarted, (_, { isNative }) => isNative)
  .reset([contentSwitched, playerLeft])

// Wire adapter events → controller effects

// Defense in depth for preview trailers (FP-4169). Primary gate is the
// `options.smartRestart === false` filter in adapter.ts — `$isTrailerFlowActive`
// alone can lose a race with `stopTrailerFlow` before an async MQ error arrives.
sample({
  clock: errorDetected,
  source: {
    isNative: $isNativeSource,
    hasPlaybackUrl: $hasPlaybackUrl,
    isPreviewTrailer: entitiesPlayerTrailerModel.$isTrailerFlowActive,
  },
  filter: ({ hasPlaybackUrl, isPreviewTrailer }) =>
    hasPlaybackUrl && !isPreviewTrailer,
  fn: ({ isNative }, error) => ({ ...error, isNative }),
  target: handleErrorFx,
})
sample({ clock: playbackRestored, target: handlePlaybackRestoredFx })
sample({ clock: contentSwitched, target: handleContentSwitchedFx })
sample({ clock: manualRetryStarted, target: handleManualRetryStartedFx })
sample({ clock: handleManualRetryStartedFx.done, target: reloadUrl })
sample({ clock: drmDetected, target: handleDrmDetectedFx })
// DRM status derived from the URL-response `drm` field (the authoritative source).
// The player source's `keySystems` are unreliable at error time, so the retry
// budget (DRM vs non-DRM) and platform-support flag are driven straight from the
// API response. Only emit when an actual playback url is present to avoid
// transient resets between retries.
sample({
  clock: api.player.$currentPlayerPageUrl,
  filter: (url) => Boolean(url?.playbackUrl),
  fn: (url) => {
    const la = url?.drm?.la
    const isDrm = Boolean(la && Object.keys(la).length > 0)

    return {
      isDrm,
      isSupported: !isDrm || isDrmSupportedByPlatform(la!),
    }
  },
  target: drmDetected,
})
sample({ clock: playerLeft, target: handlePlayerLeftFx })
